import React, { useEffect, useState } from 'react';
import { VerticalTimeline, VerticalTimelineElement } from 'react-vertical-timeline-component';
import 'react-vertical-timeline-component/style.min.css';
import { MdOutlineWork as WorkIcon } from 'react-icons/md';
import { IoSchool as SchoolIcon } from 'react-icons/io5';
import { FaStar as StarIcon } from 'react-icons/fa';
import './WorkExperience.css';
import { TimelineItem } from '../types';
import { getTimeline } from '../queries/getTimeline';

// BluWis uses the same blue work-card styling as the existing experience entries.
const BLUWIS_EXPERIENCE: TimelineItem = {
  timelineType: "work",
  name: "Cloud / AI-ML Intern",
  title: "BluWis — AI/ML Engineer",
  techStack: "Python, AWS S3, AWS Lambda",
  summaryPoints: [
    "Built and worked on a weather-data ingestion pipeline, transforming raw API/XLSX data into structured day-wise datasets.",
    "Developed Python-based data processing and automation workflows for cleaning, transforming, scoring, and storing weather data.",
    "Integrated AWS S3 and AWS Lambda for cloud-based data ingestion and processing.",
    "Worked with structured datasets and maintained outputs for ML/data analytics workflows."
  ],
  dateRange: "2026 (July) - Present · Texas, United States"
};

// Fallback data for timeline with your information
const FALLBACK_TIMELINE_DATA: TimelineItem[] = [
  BLUWIS_EXPERIENCE,
  {
    timelineType: "work",
    name: "App Developer",
    title: "LetsUpgrade",
    techStack: "React Native, Expo Go",
    summaryPoints: ["Developed mobile applications launched on PlayStore", "Built cross-platform apps using React Native and Expo Go"],
    dateRange: "2024 (August) - 2024 (October)"
  },
  {
    timelineType: "education",
    name: "ITM Skills University",
    title: "Computer Science",
    techStack: "",
    summaryPoints: ["Pursuing degree in Computer Science", "Focused on software development and AI technologies"],
    dateRange: "2024 - 2028"
  }
];

const prepareTimelineData = (items: TimelineItem[]): TimelineItem[] => {
  const updatedItems = items
    .filter((item) => item.title !== BLUWIS_EXPERIENCE.title)
    .map((item) =>
      item.title === "LetsUpgrade"
        ? { ...item, dateRange: "2024 (August) - 2024 (October)" }
        : item
    );

  return [BLUWIS_EXPERIENCE, ...updatedItems];
};

const WorkExperience: React.FC = () => {
  const [timeLineData, setTimeLineData] = useState<TimelineItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchTimelineItem() {
      try {
        const data = await getTimeline();
        setTimeLineData(prepareTimelineData(data));
      } catch (err) {
        console.error("Failed to fetch timeline data:", err);
        setError("Failed to load timeline data. Showing sample content.");
        // Use fallback data when API request fails
        setTimeLineData(FALLBACK_TIMELINE_DATA);
      } finally {
        setLoading(false);
      }
    }
    fetchTimelineItem();
  }, []);

  if (loading) return <div className="timeline-loading">Loading experience timeline...</div>;
  
  if (error) {
    console.warn(error);
  }

  // If we still don't have data for some reason, use fallback
  const displayData = timeLineData || FALLBACK_TIMELINE_DATA;

  return (
    <>
      <div className="timeline-container">
        <h2 className="timeline-title domain-heading">📅 Vinayak Singh's Experience & Education</h2>
        <p className="timeline-subtitle">AI & Full Stack Innovator | Building Products that Think, Learn & Scale</p>
      </div>
      <VerticalTimeline>
        {displayData.map((item, index) => (
          <VerticalTimelineElement
            key={index}
            className={`vertical-timeline-element--${item.timelineType}`}
            contentStyle={
              item.timelineType === "work"
                ? { background: 'rgb(33, 150, 243)', color: '#fff' } // Standard blue for work
                : { background: 'rgb(156, 39, 176)', color: '#fff' } // Purple for education
            }
            contentArrowStyle={
              item.timelineType === "work"
                ? { borderRight: '7px solid rgb(33, 150, 243)' } // Standard blue for work
                : { borderRight: '7px solid rgb(156, 39, 176)' } // Purple for education
            }
            date={item.dateRange}
            iconStyle={
              item.timelineType === "work"
                ? { background: 'rgb(33, 150, 243)', color: '#fff' } // Standard blue for work icon
                : { background: 'rgb(156, 39, 176)', color: '#fff' } // Purple for education icon
            }
            icon={item.timelineType === "work" ? <WorkIcon /> : <SchoolIcon />}
          >
            {item.timelineType === "work" ? (
              <div style={{ color: 'white' }}>
                <h3 className="vertical-timeline-element-title">{item.title}</h3>
                <h4 className="vertical-timeline-element-subtitle">{item.name}</h4>
                <p className="vertical-timeline-element-tech">🔧 {item.techStack}</p>
                <ul className="timeline-summary-list">
                  {item.summaryPoints.map((point, pointIndex) => (
                    <li key={pointIndex}>{point}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <div style={{ color: 'white' }}>
                <h3 className="vertical-timeline-element-title">{item.name}</h3>
                <h4 className="vertical-timeline-element-subtitle">{item.title}</h4>
                <p>{item.summaryPoints.join(', ')}</p>
              </div>
            )}
          </VerticalTimelineElement>
        ))}
        <VerticalTimelineElement
          iconStyle={{ background: 'rgb(255, 193, 7)', color: '#000' }} // Amber for star icon
          icon={<StarIcon />}
        />
      </VerticalTimeline>
    </>
  );
};

export default WorkExperience;